import { useLocation, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  LayoutDashboard,
  Users,
  FileText,
  Receipt,
  Telescope,
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/components/ui/utils';

interface TabItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * Mobile-only bottom tab bar for the 3-4 most-used destinations.
 * Hidden at >=md (the sidebar drawer takes over there). Mirrors AppSidebar's
 * permission logic so it never shows a destination the user can't reach.
 */
export function BottomTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = usePermissions();

  if (permissions.isPending) return null;

  const showPersonalDashboard =
    permissions.isExternalPartner ||
    ((permissions.isTeamMember || permissions.isTeamLead) && !permissions.canViewAllProjects);

  const items: TabItem[] = [];

  if (permissions.canViewProjects) {
    items.push(
      showPersonalDashboard
        ? { title: 'Dashboard', url: '/my-dashboard', icon: LayoutDashboard }
        : { title: 'Portfolio', url: '/', icon: Briefcase },
    );
  }
  if (permissions.canViewCRM) {
    items.push({ title: 'Pipeline', url: '/crm', icon: Users });
  }
  if (permissions.isSuperAdmin || permissions.isOwner) {
    items.push({ title: 'Reports', url: '/reports', icon: FileText });
    items.push({ title: 'Billing', url: '/billing', icon: Receipt });
  }
  // Last on purpose. Owners already fill the four slots, so this only surfaces
  // for the pipeline roles — who otherwise had no mobile route to /prospects.
  if (permissions.canManagePipeline) {
    items.push({ title: 'Prospects', url: '/prospects', icon: Telescope });
  }

  const tabs = items.slice(0, 4);
  if (tabs.length === 0) return null;

  return (
    <nav
      aria-label="Primary"
      className="md:hidden shrink-0 border-t border-border bg-background pb-[max(0.5rem,env(safe-area-inset-bottom))]"
    >
      <div className="flex items-stretch justify-around">
        {tabs.map((tab) => {
          const isActive =
            location.pathname === tab.url ||
            (tab.url !== '/' && location.pathname.startsWith(tab.url));
          return (
            <button
              key={tab.url}
              type="button"
              onClick={() => navigate(tab.url)}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 px-1 pt-2.5 pb-1.5 text-[11px] transition-colors',
                isActive
                  ? 'text-foreground font-semibold'
                  : 'text-muted-foreground font-medium',
              )}
            >
              {isActive && (
                <span
                  aria-hidden
                  className="absolute top-0 h-0.5 w-8 rounded-full bg-[var(--color-gold)]"
                />
              )}
              <tab.icon className="size-5" />
              <span className="leading-none">{tab.title}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
